import React, { useState, useEffect, useRef } from 'react'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Badge } from '../components/ui/badge'
import { useAuthStore } from '../stores/authStore'
import { API_CONFIG } from '../lib/config'
import { Bot, Send, User, Trash2, MessageSquare, Brain } from 'lucide-react'
import { toast } from 'sonner'

interface ChatMessage {
    id: string
    userMessage: string
    botResponse: string
    timestamp: string
}

interface ChatHistory {
    history: ChatMessage[]
    total: number
    hasMore: boolean
}

export default function ChatbotPage() {
    const { accessToken } = useAuthStore()
    const [message, setMessage] = useState('')
    const [messages, setMessages] = useState<ChatMessage[]>([])
    const [loading, setLoading] = useState(false)
    const [history, setHistory] = useState<ChatMessage[]>([])
    const [showHistory, setShowHistory] = useState(false)
    const messagesEndRef = useRef<HTMLDivElement>(null)

    // Mesajları scroll et
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }

    useEffect(() => {
        scrollToBottom()
    }, [messages])

    // Chat geçmişini yükle
    const loadHistory = async () => {
        try {
            const response = await fetch(`${API_CONFIG.BASE_URL}/api/chatbot/history?limit=50`, {
                headers: {
                    'Authorization': `Bearer ${accessToken}`
                }
            })

            if (response.ok) {
                const data: ChatHistory = await response.json()
                setHistory(data.history)
            } else {
                toast.error('Chat geçmişi yüklenemedi')
            }
        } catch (error) {
            toast.error('Chat geçmişi yüklenemedi')
        }
    }

    // Mesaj gönder
    const sendMessage = async () => {
        if (!message.trim()) return

        const userMessage = message.trim()
        setMessage('')
        setLoading(true)

        // Kullanıcı mesajını ekle
        const userMsg: ChatMessage = {
            id: Date.now().toString(),
            userMessage,
            botResponse: '',
            timestamp: new Date().toISOString()
        }
        setMessages(prev => [...prev, userMsg])

        try {
            const response = await fetch(`${API_CONFIG.BASE_URL}/api/chatbot/chat`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`
                },
                body: JSON.stringify({ message: userMessage })
            })

            if (response.ok) {
                const data = await response.json()

                // Bot yanıtını ekle
                const botMsg: ChatMessage = {
                    id: (Date.now() + 1).toString(),
                    userMessage: '',
                    botResponse: data.response,
                    timestamp: data.timestamp
                }
                setMessages(prev => [...prev, botMsg])
            } else {
                const error = await response.json()
                toast.error(error.error || 'Chatbot hatası')
            }
        } catch (error) {
            toast.error('Chatbot hatası')
        } finally {
            setLoading(false)
        }
    }

    // Enter ile mesaj gönder
    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            sendMessage()
        }
    }

    // Geçmişi temizle
    const clearHistory = () => {
        setMessages([])
        setHistory([])
        toast.success('Chat geçmişi temizlendi')
    }

    // Geçmişi göster/gizle
    const toggleHistory = () => {
        setShowHistory(!showHistory)
        if (!showHistory) {
            loadHistory()
        }
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="container mx-auto p-2 sm:p-4 md:p-6 max-w-7xl">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-4 sm:mb-6">
                    <div className="flex items-center space-x-2">
                        <Brain className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
                        <h1 className="text-lg sm:text-xl md:text-2xl font-bold">AI Asistan</h1>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2">
                        <Button variant="outline" onClick={toggleHistory} className="w-full sm:w-auto text-xs sm:text-sm h-8 sm:h-9">
                            <MessageSquare className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                            <span className="hidden xs:inline">{showHistory ? 'Chat\'i Göster' : 'Geçmişi Göster'}</span>
                            <span className="xs:hidden">{showHistory ? 'Chat' : 'Geçmiş'}</span>
                        </Button>
                        <Button variant="outline" onClick={clearHistory} className="w-full sm:w-auto text-xs sm:text-sm h-8 sm:h-9">
                            <Trash2 className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                            Temizle
                        </Button>
                    </div>
                </div>

                {!showHistory ? (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
                        {/* Chat Alanı */}
                        <div className="lg:col-span-2">
                            <Card className="h-[450px] sm:h-[550px] md:h-[650px] lg:h-[700px] flex flex-col">
                                <CardHeader className="pb-2 sm:pb-3 md:pb-4 px-3 sm:px-4 md:px-6 flex-shrink-0">
                                    <CardTitle className="flex items-center text-base sm:text-lg md:text-xl">
                                        <Bot className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 mr-2 text-green-600" />
                                        AI Asistan
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="flex-1 flex flex-col p-3 sm:p-4 md:p-6 min-h-0">
                                    {/* Mesajlar */}
                                    <div className="flex-1 overflow-y-auto space-y-3 sm:space-y-4 mb-3 sm:mb-4 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                                        {messages.length === 0 ? (
                                            <div className="text-center text-gray-500 py-6 sm:py-8">
                                                <Bot className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 sm:mb-4 text-gray-300" />
                                                <p className="text-xs sm:text-sm md:text-base">AI asistanınıza soru sorun!</p>
                                                <p className="text-xs text-gray-400 mt-1 sm:mt-2 px-2">
                                                    Stok, ürün, sipariş ve istatistik verileriniz hakkında sorular sorabilirsiniz.
                                                </p>
                                            </div>
                                        ) : (
                                            messages.map((msg) => (
                                                <div key={msg.id} className="space-y-2">
                                                    {/* Kullanıcı Mesajı */}
                                                    {msg.userMessage && (
                                                        <div className="flex justify-end">
                                                            <div className="bg-blue-600 text-white rounded-lg p-2 sm:p-3 max-w-[90%] sm:max-w-[80%] lg:max-w-[75%]">
                                                                <div className="flex items-center space-x-1 sm:space-x-2 mb-1">
                                                                    <User className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                                                                    <span className="text-xs font-medium">Siz</span>
                                                                </div>
                                                                <p className="text-xs sm:text-sm md:text-base break-words leading-relaxed">{msg.userMessage}</p>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Bot Yanıtı */}
                                                    {msg.botResponse && (
                                                        <div className="flex justify-start">
                                                            <div className="bg-gray-100 text-gray-900 rounded-lg p-2 sm:p-3 max-w-[90%] sm:max-w-[80%] lg:max-w-[75%]">
                                                                <div className="flex items-center space-x-1 sm:space-x-2 mb-1">
                                                                    <Bot className="w-3 h-3 sm:w-4 sm:h-4 text-green-600 flex-shrink-0" />
                                                                    <span className="text-xs font-medium text-green-600">AI Asistan</span>
                                                                </div>
                                                                <p className="text-xs sm:text-sm md:text-base whitespace-pre-wrap break-words leading-relaxed">{msg.botResponse}</p>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            ))
                                        )}
                                        {loading && (
                                            <div className="flex justify-start">
                                                <div className="bg-gray-100 text-gray-900 rounded-lg p-2 sm:p-3">
                                                    <div className="flex items-center space-x-1 sm:space-x-2">
                                                        <Bot className="w-3 h-3 sm:w-4 sm:h-4 text-green-600" />
                                                        <span className="text-xs font-medium text-green-600">AI Asistan</span>
                                                    </div>
                                                    <div className="flex items-center space-x-1 mt-2">
                                                        <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-gray-400 rounded-full animate-bounce"></div>
                                                        <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                                                        <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        <div ref={messagesEndRef} />
                                    </div>

                                    {/* Mesaj Gönderme */}
                                    <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                                        <Input
                                            placeholder="AI asistanınıza soru sorun..."
                                            value={message}
                                            onChange={(e) => setMessage(e.target.value)}
                                            onKeyPress={handleKeyPress}
                                            disabled={loading}
                                            className="flex-1 text-xs sm:text-sm min-h-[40px]"
                                        />
                                        <Button
                                            onClick={sendMessage}
                                            disabled={loading || !message.trim()}
                                            className="px-3 sm:px-4 h-10 sm:h-auto"
                                            size="sm"
                                        >
                                            <Send className="w-4 h-4 sm:w-4 sm:h-4 mr-1 sm:mr-0" />
                                            <span className="sm:hidden ml-1">Gönder</span>
                                        </Button>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Yardım Paneli */}
                        <div className="lg:col-span-1">
                            <Card className="h-[450px] sm:h-[550px] md:h-[650px] lg:h-[700px] flex flex-col">
                                <CardHeader className="pb-2 sm:pb-3 md:pb-4 px-3 sm:px-4 md:px-6 flex-shrink-0">
                                    <CardTitle className="text-sm sm:text-base md:text-lg">Yardım</CardTitle>
                                </CardHeader>
                                <CardContent className="flex-1 px-3 sm:px-4 md:px-6 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                                    <div className="space-y-3 sm:space-y-4">
                                        <div>
                                            <h4 className="font-medium text-xs sm:text-sm text-gray-700 mb-1 sm:mb-2">💡 Nasıl Net Soru Sorulur:</h4>
                                            <div className="space-y-2 mb-3">
                                                <div className="text-xs sm:text-sm text-amber-700 bg-amber-50 p-2 rounded border-l-4 border-amber-400">
                                                    <strong>✅ İyi Örnek:</strong> "En çok stoğu olan 1 ürünü ver"
                                                </div>
                                                <div className="text-xs sm:text-sm text-red-700 bg-red-50 p-2 rounded border-l-4 border-red-400">
                                                    <strong>❌ Kötü Örnek:</strong> "ürünler hakkında bilgi"
                                                </div>
                                            </div>

                                            <h4 className="font-medium text-xs sm:text-sm text-gray-700 mb-1 sm:mb-2">Örnek Sorular:</h4>
                                            <div className="space-y-1 sm:space-y-2">
                                                <div className="text-xs sm:text-sm text-gray-600 p-2 bg-gray-50 rounded text-center sm:text-left">
                                                    "En çok stoğu olan 1 ürünü ver"
                                                </div>
                                                <div className="text-xs sm:text-sm text-gray-600 p-2 bg-gray-50 rounded text-center sm:text-left">
                                                    "Son 2 günde stoğu 10'dan fazla düşen ürünler"
                                                </div>
                                                <div className="text-xs sm:text-sm text-gray-600 p-2 bg-gray-50 rounded text-center sm:text-left">
                                                    "Stokta olmayan ürün sayısı kaç?"
                                                </div>
                                                <div className="text-xs sm:text-sm text-gray-600 p-2 bg-gray-50 rounded text-center sm:text-left">
                                                    "Altın yüzük ara"
                                                </div>
                                                <div className="text-xs sm:text-sm text-gray-600 p-2 bg-gray-50 rounded text-center sm:text-left">
                                                    "Fiyatı 1000'den yüksek ürünler"
                                                </div>
                                            </div>
                                        </div>

                                        <div>
                                            <h4 className="font-medium text-xs sm:text-sm text-gray-700 mb-1 sm:mb-2">📋 Soru Sorma İpuçları:</h4>
                                            <div className="space-y-1 text-xs sm:text-sm text-gray-600">
                                                <div className="flex items-start space-x-2">
                                                    <span className="text-blue-500 font-bold">•</span>
                                                    <span>Spesifik olun: "En çok stoğu olan 1 ürün"</span>
                                                </div>
                                                <div className="flex items-start space-x-2">
                                                    <span className="text-blue-500 font-bold">•</span>
                                                    <span>Sayısal değerler belirtin: "10'dan fazla", "1000'den yüksek"</span>
                                                </div>
                                                <div className="flex items-start space-x-2">
                                                    <span className="text-blue-500 font-bold">•</span>
                                                    <span>Zaman aralığı verin: "Son 2 günde", "Bu hafta"</span>
                                                </div>
                                                <div className="flex items-start space-x-2">
                                                    <span className="text-blue-500 font-bold">•</span>
                                                    <span>Ürün özelliklerini kullanın: "altın", "yüzük", "SKU-123"</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div>
                                            <h4 className="font-medium text-xs sm:text-sm text-gray-700 mb-1 sm:mb-2">Özellikler:</h4>
                                            <div className="space-y-1">
                                                <div className="flex items-center space-x-2 text-xs sm:text-sm text-gray-600">
                                                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-green-500 rounded-full flex-shrink-0"></div>
                                                    <span>Gerçek zamanlı veri</span>
                                                </div>
                                                <div className="flex items-center space-x-2 text-xs sm:text-sm text-gray-600">
                                                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-blue-500 rounded-full flex-shrink-0"></div>
                                                    <span>Stok analizi</span>
                                                </div>
                                                <div className="flex items-center space-x-2 text-xs sm:text-sm text-gray-600">
                                                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-purple-500 rounded-full flex-shrink-0"></div>
                                                    <span>Satış istatistikleri</span>
                                                </div>
                                                <div className="flex items-center space-x-2 text-xs sm:text-sm text-gray-600">
                                                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-orange-500 rounded-full flex-shrink-0"></div>
                                                    <span>Ürün performansı</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                ) : (
                    /* Chat Geçmişi */
                    <Card className="h-[450px] sm:h-[550px] md:h-[650px] lg:h-[700px] flex flex-col">
                        <CardHeader className="pb-2 sm:pb-3 md:pb-4 px-3 sm:px-4 md:px-6 flex-shrink-0">
                            <CardTitle className="text-base sm:text-lg md:text-xl">Chat Geçmişi</CardTitle>
                        </CardHeader>
                        <CardContent className="flex-1 px-3 sm:px-4 md:px-6 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                            {history.length === 0 ? (
                                <div className="text-center py-4 sm:py-6 md:py-8 text-gray-500">
                                    <MessageSquare className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 mx-auto mb-3 sm:mb-4 text-gray-300" />
                                    <div className="text-xs sm:text-sm md:text-base">Chat geçmişi bulunamadı</div>
                                </div>
                            ) : (
                                <div className="space-y-3 sm:space-y-4">
                                    {history.map((chat) => (
                                        <div key={chat.id} className="border rounded-lg p-2 sm:p-3 md:p-4">
                                            <div className="space-y-2 sm:space-y-3">
                                                {/* Kullanıcı Mesajı */}
                                                <div className="flex justify-end">
                                                    <div className="bg-blue-600 text-white rounded-lg p-2 sm:p-3 max-w-[90%] sm:max-w-[80%] lg:max-w-[75%]">
                                                        <div className="flex items-center space-x-1 sm:space-x-2 mb-1">
                                                            <User className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                                                            <span className="text-xs font-medium">Siz</span>
                                                        </div>
                                                        <p className="text-xs sm:text-sm md:text-base break-words leading-relaxed">{chat.userMessage}</p>
                                                    </div>
                                                </div>

                                                {/* Bot Yanıtı */}
                                                <div className="flex justify-start">
                                                    <div className="bg-gray-100 text-gray-900 rounded-lg p-2 sm:p-3 max-w-[90%] sm:max-w-[80%] lg:max-w-[75%]">
                                                        <div className="flex items-center space-x-1 sm:space-x-2 mb-1">
                                                            <Bot className="w-3 h-3 sm:w-4 sm:h-4 text-green-600 flex-shrink-0" />
                                                            <span className="text-xs font-medium text-green-600">AI Asistan</span>
                                                        </div>
                                                        <p className="text-xs sm:text-sm md:text-base whitespace-pre-wrap break-words leading-relaxed">{chat.botResponse}</p>
                                                    </div>
                                                </div>

                                                {/* Tarih */}
                                                <div className="text-right">
                                                    <span className="text-xs text-gray-500">
                                                        {new Date(chat.timestamp).toLocaleString('tr-TR')}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                )}
            </div>
        </div>
    )
}
